import * as Sentry from '@sentry/react';
import { AxiosInstance, getAdapter, isAxiosError, isCancel } from 'axios';

export type RequestTrace = { traceId: string; spanId: string };

const TRACEPARENT_PATTERN = /^[0-9a-f]{2}-([0-9a-f]{32})-([0-9a-f]{16})-[0-9a-f]{2}$/;

/** Parses a W3C `traceparent` header value: `00-<32-hex traceId>-<16-hex spanId>-<flags>`. */
export const parseTraceparent = (traceparent: unknown): RequestTrace | undefined => {
  const match = typeof traceparent === 'string' ? traceparent.match(TRACEPARENT_PATTERN) : undefined;
  return match ? { traceId: match[1], spanId: match[2] } : undefined;
};

/** Reads the trace a request was sent with, off the request config `traceApiRequests` stamped. */
export const getRequestTrace = (config?: { headers?: unknown }): RequestTrace | undefined =>
  parseTraceparent((config?.headers as Record<string, unknown> | undefined)?.['traceparent']);

type PageTrace = {
  context: ReturnType<Sentry.Span['spanContext']>;
  startTimestamp: number;
  sampled: boolean;
  sampleRand: number;
  sampleRate?: number;
};

// The current page view (the latest pageload or navigation), kept after its span ends so API calls
// made later on the same page are still tied to it. Only ids are kept, not the span and its children.
let currentPage: PageTrace | undefined;

// Sentry only links to a previous trace that started within the last hour.
const PREVIOUS_TRACE_MAX_AGE_SECONDS = 3600;

/**
 * Tracks pageload/navigation spans, so every API request can link to the page that made it and
 * share its sampling decision (see `traceApiRequests`).
 *
 * Also takes over Sentry's `linkPreviousTrace`, which must be turned off (see sentry.ts): it links
 * every root span to the one before, and with a root span per API call that would chain the page
 * traces through API calls. Here each page links straight to the previous page, as Sentry would
 * without per-request traces.
 */
export const trackPageTraces = (client: Sentry.BrowserClient): void => {
  client.on('spanStart', (span) => {
    const { op, start_timestamp: startTimestamp } = Sentry.spanToJSON(span);
    if (Sentry.getRootSpan(span) !== span || (op !== 'pageload' && op !== 'navigation')) return;

    const previous = currentPage;
    if (previous && startTimestamp - previous.startTimestamp <= PREVIOUS_TRACE_MAX_AGE_SECONDS) {
      const { traceId, spanId } = previous.context;
      span.addLink({ context: previous.context, attributes: { 'sentry.link.type': 'previous_trace' } });
      // Sentry reads this attribute to show previous/next trace navigation in the trace view.
      span.setAttribute('sentry.previous_trace', `${traceId}-${spanId}-${previous.sampled ? 1 : 0}`);
    }

    const context = span.spanContext();
    const sampleRate = Number(Sentry.spanToJSON(span).data['sentry.sample_rate']);
    currentPage = {
      context,
      startTimestamp,
      sampled: context.traceFlags === 1,
      sampleRand: Sentry.getCurrentScope().getPropagationContext().sampleRand,
      sampleRate: Number.isNaN(sampleRate) ? undefined : sampleRate
    };
  });
};

/** Forgets the tracked page. For tests. */
export const resetPageTrace = (): void => {
  currentPage = undefined;
};

/**
 * Runs every request made through `axiosInstance` in its own trace, so the frontend and the
 * backend share one trace id per API call, never one per page or session.
 *
 * Each request gets a new trace (`startNewTrace`) with an `http.client` root span, and the
 * request carries that span in three headers:
 *   - `traceparent`: read by ASP.NET Core, which makes it the request's `Activity` — so it is the
 *     TraceId Serilog writes to Log Analytics, the `x-correlation-id` the API returns, and what
 *     Dapr propagates to the downstream services.
 *   - `sentry-trace` and `baggage`: Sentry's own headers. Set here because Sentry's XHR
 *     instrumentation would otherwise add them from the page's trace instead.
 *
 * `traceparent` is always sent as sampled (`01`): Sentry's sampling only decides which of these
 * spans Sentry keeps, and must not make the backend drop its side of the trace.
 *
 * Each request span is tied to the page that made it (see `trackPageTraces`):
 *   - a span link plus a searchable `page.trace_id` attribute, so a page's API calls can be found
 *     from its pageload/navigation trace;
 *   - the page's sampling decision, so Sentry keeps a page's API calls exactly when it keeps the page.
 *
 * Sentry's XHR instrumentation must not create spans for these requests (see
 * `shouldCreateSpanForRequest` in sentry.ts), or every call would get a duplicate child span.
 * Without a Sentry client (no DSN, e.g. local dev) the span is non-recording but still has a
 * fresh trace id, so the backend still gets a unique trace per request.
 */
export const traceApiRequests = (axiosInstance: AxiosInstance): void => {
  const adapter = getAdapter(axiosInstance.defaults.adapter);

  axiosInstance.defaults.adapter = (config) => {
    const method = (config.method ?? 'get').toUpperCase();
    const path = (config.url ?? '').split(/[?#]/)[0];

    const page = currentPage;

    return Sentry.startNewTrace(() => {
      if (page) {
        // Inside startNewTrace this is the new trace's own scope. Sentry samples a root span by the
        // propagation context's `sampled` when it is set, as if the page were its parent, so the
        // request gets exactly the page's decision. The page's sample rand keeps the trace data
        // consistent with it.
        const scope = Sentry.getCurrentScope();
        scope.setPropagationContext({
          ...scope.getPropagationContext(),
          sampled: page.sampled,
          sampleRand: page.sampleRand
        });
      }

      return Sentry.startSpan(
        {
          name: `${method} ${path}`,
          op: 'http.client',
          attributes: {
            'http.method': method,
            'http.url': axiosInstance.getUri({ ...config, params: undefined }),
            'sentry.origin': 'manual.http.apisauce',
            ...(page ? { 'page.trace_id': page.context.traceId } : {}),
            // An inherited decision has no sample rate of its own. Sentry reads this one into the
            // trace's sample_rate, which it needs to extrapolate span counts (same as `consistentTraceSampling`).
            ...(page?.sampleRate !== undefined ? { 'sentry.previous_trace_sample_rate': page.sampleRate } : {})
          },
          links: page ? [{ context: page.context, attributes: { 'sentry.link.type': 'page' } }] : undefined
        },
        async (span) => {
          const { traceId, spanId } = span.spanContext();
          config.headers.set('traceparent', `00-${traceId}-${spanId}-01`);

          const { 'sentry-trace': sentryTrace, baggage } = Sentry.getTraceData({ span });
          if (sentryTrace) config.headers.set('sentry-trace', sentryTrace);
          if (baggage) config.headers.set('baggage', baggage);

          try {
            const response = await adapter(config);
            Sentry.setHttpStatus(span, response.status);
            return response;
          } catch (error) {
            if (isCancel(error)) {
              // Aborted on purpose (e.g. React Query cancelling a stale query), not a failure.
              span.setStatus({ code: 2, message: 'cancelled' });
            } else if (isAxiosError(error) && error.response) {
              Sentry.setHttpStatus(span, error.response.status);
            } else {
              span.setStatus({ code: 2, message: 'internal_error' });
            }
            throw error;
          }
        }
      );
    });
  };
};
